import React, {
  useState,
  useEffect,
  Fragment,
  useRef,
  useCallback
} from 'react'
import injectSheet from 'react-jss'
import { Presentation } from 'pobble-components/dist/presentation'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowAltLeft } from '@fortawesome/pro-solid-svg-icons/faArrowAltLeft'
import { faArrowAltRight } from '@fortawesome/pro-solid-svg-icons/faArrowAltRight'
import { faExpand } from '@fortawesome/pro-solid-svg-icons/faExpand'
import { faCompress } from '@fortawesome/pro-solid-svg-icons/faCompress'
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes'
import { faAngleLeft } from '@fortawesome/pro-solid-svg-icons/faAngleLeft'
import classNames from 'classnames'
import screenfull from 'screenfull'
import { usePresentationNavigation } from 'pobble-components/dist/presentation'
import ButtonElement from 'pobble-components/dist/button-element'
import { usePresentMode } from './present-mode'
import Tooltip from 'pobble-components/dist/tooltip'
import { SubsCrown } from 'pobble-components/dist/subscriptions'
import Button from 'pobble-components/dist/button'
import { useSubscriptionDetails } from 'pobble-components/dist/subscriptions/utils'
import posthog from 'posthog-js'

const controlButtonStyles = ({ COLORS }) => ({
  container: {
    userSelect: 'none',
    cursor: 'pointer',
    fontSize: '60px',
    border: `.05em solid ${COLORS.BLUE.NORMAL}`,
    borderRadius: '1em',
    background: '#fff',
    height: '1em',
    width: '1em',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COLORS.BLUE.NORMAL,
    '&:hover': {
      opacity: '1'
    },
    '& > *': {
      fontSize: '.6em'
    }
  },
  downscale: {
    '& > *': {
      fontSize: '.5em'
    }
  }
})

const ControlButton = injectSheet(controlButtonStyles)(
  ({
    icon,
    downscale,
    classes,
    disabled,
    onClick,
    className,
    theme,
    ...props
  }) => {
    return (
      <ButtonElement
        onClick={!disabled ? onClick : undefined}
        className={classNames(
          classes.container,
          {
            [classes.disabled]: disabled,
            [classes.downscale]: downscale
          },
          className
        )}
        {...props}>
        <FontAwesomeIcon icon={icon} />
      </ButtonElement>
    )
  }
)

const directPresentStyles = {
  container: {
    height: '100vh',
    width: '100vw',
    position: 'fixed',
    left: '0',
    top: '0',
    zIndex: '9999',
    background: '#000',
    '& > div:first-child': {
      opacity: '0'
    }
  },
  loaded: {
    '& > div:first-child': {
      animation: 'appear .3s forwards'
    }
  },
  slideContainer: {
    padding: '0',
    height: '100vh'
  },
  controls: {
    position: 'absolute',
    right: '0',
    bottom: '0',
    padding: '20px',
    display: 'flex',
    transition: 'transform .2s',
    '& > *': {
      marginLeft: '20px'
    }
  },
  hideControls: {
    transform: 'translateX(242px)',
    '& $hideControlsButton': {
      fontSize: '32px',
      opacity: '.8'
    }
  },
  hideControlsButton: {
    color: '#fff',
    textShadow: '0 0 6px rgba(0, 0, 0, .6)',
    fontWeight: 'bold'
  },
  closeButton: {
    position: 'absolute',
    right: '20px',
    top: '20px',
    fontSize: '30px'
  },
  '@keyframes appear': {
    from: {
      opacity: '0'
    },
    to: {
      opacity: '1'
    }
  },
  restrictedFullScreen: {
    position: 'absolute',
    background: 'rgba(0,0,0,0.8)',
    width: '100%',
    height: '100%',
    top: 0,
    backdropFilter: 'blur(4px)'
  },
  slideRestrictedCTA: {
    top: '50%',
    position: 'absolute',
    left: '50%',
    transform: 'translate(-50%,-50%)',
    maxWidth: '90%',
    minHeight: '80px',
    [`@media (min-width: 1023px)`]: {
      maxWidth: '30%'
    }
  }
}

const DirectPresent = ({ classes, onClose, data, onSlideUpdate }) => {
  const presentation = data && data !== 'pending' ? data : null
  const isPending = data === 'pending'

  const [isFs, setFs] = useState(false)

  const containerRef = useRef(null)

  const { options } = usePresentMode()
  const [hideControls, setHideControls] = useState(false)

  const exit = useCallback(
    () => {
      screenfull.enabled && screenfull.isFullscreen && screenfull.exit()
      onClose()
    },
    [onClose]
  )

  const {
    activeSlideId,
    isFirstSlide,
    isLastSlide,
    nextSlide,
    prevSlide
  } = usePresentationNavigation(presentation, {
    activeSlideId: options && options.activeSlideId
  })

  useEffect(
    () => {
      const listener = e => {
        const tagName = e.target.tagName
        const keyCode = e.keyCode
        if (tagName !== 'TEXTAREA' && tagName !== 'INPUT') {
          switch (keyCode) {
            case 39:
            case 32:
            case 13:
              nextSlide()
              break
            case 37:
              prevSlide()
              break
            case 27:
              exit()
              break
            default:
              break
          }
        }
      }
      document.addEventListener('keydown', listener)
      return () => document.removeEventListener('keydown', listener)
    },
    [data, nextSlide, prevSlide, exit]
  )

  useEffect(() => {
    if (screenfull.enabled) {
      const listener = () => setFs(screenfull.isFullscreen)
      screenfull.on('change', listener)
      return () => screenfull.off('change', listener)
    }
  }, [])

  const toggleFS = useCallback(() => {
    if (screenfull.enabled) {
      if (!screenfull.isFullscreen) {
        screenfull.request()
      } else {
        screenfull.exit()
      }
    }
  }, [])

  const { hasAccessToTL } = useSubscriptionDetails()

  return (
    <div
      className={`${classes.container} ${!isPending ? classes.loaded : ''}`}
      ref={containerRef}
      style={{
        height: '-webkit-fill-available' // fix iPad viewport
      }}>
      <Fragment>
        {presentation &&
          !!activeSlideId && (
            <>
              <Presentation
                onSlideUpdate={onSlideUpdate}
                slideProps={{
                  containerClassName: classes.slideContainer
                }}
                data={presentation}
                activeSlideId={activeSlideId}
              />
              {!hasAccessToTL &&
                presentation &&
                activeSlideId &&
                presentation.slides.findIndex(s => s.id === activeSlideId) &&
                presentation.slides[
                  presentation.slides.findIndex(s => s.id === activeSlideId)
                ].restricted && (
                  <div className={classes.restrictedFullScreen}>
                    <Button
                      big
                      wide
                      icon={<SubsCrown accent />}
                      onClick={() => {
                        posthog.capture(
                          'clicked on slide upsell full screen cta'
                        )
                        window.location.href =
                          '/account/membership?from=fullscreen-slide'
                      }}
                      className={classes.slideRestrictedCTA}>
                      Unlock Pobble Premium
                    </Button>
                  </div>
                )}
              <div
                className={classNames(
                  classes.controls,
                  'metrics__presentation-controls',
                  { [classes.hideControls]: hideControls }
                )}>
                <ButtonElement
                  onClick={() => setHideControls(!hideControls)}
                  className={classes.hideControlsButton}>
                  {!hideControls && 'Hide controls'}
                  {hideControls && (
                    <Tooltip content="Use keyboard arrows to navigate. Click to show controls">
                      <FontAwesomeIcon icon={faAngleLeft} />
                    </Tooltip>
                  )}
                </ButtonElement>
                <ControlButton
                  onClick={toggleFS}
                  downscale
                  className={classNames(
                    classes.fsButton,
                    'metrics__present-mode-fullscreen-button'
                  )}
                  icon={!isFs ? faExpand : faCompress}
                />
                {!presentation.asset && (
                  <>
                    <ControlButton
                      className={classNames(
                        classes.prevButton,
                        'metrics__present-mode-arrow-button'
                      )}
                      disabled={isFirstSlide}
                      icon={faArrowAltLeft}
                      onClick={() => {
                        prevSlide()
                      }}
                    />
                    <ControlButton
                      className="metrics__present-mode-arrow-button"
                      disabled={isLastSlide}
                      icon={faArrowAltRight}
                      onClick={() => {
                        nextSlide()
                      }}
                    />
                  </>
                )}
              </div>
            </>
          )}

        <ControlButton
          className={classNames(
            classes.closeButton,
            'metrics__presentation-close'
          )}
          icon={faTimes}
          onClick={() => {
            screenfull.enabled && screenfull.isFullscreen && screenfull.exit()
            onClose()
          }}
        />
      </Fragment>
    </div>
  )
}

export default injectSheet(directPresentStyles)(DirectPresent)
